/**
 * Recovery for Next.js `ChunkLoadError`.
 *
 * When a new version is deployed, the hashed JS chunk filenames change. A client
 * still holding a stale HTML/RSC payload (open tab, Service-Worker-cached page,
 * or a deployment-skew window that has elapsed) requests chunks that no longer
 * exist on the server and gets a 404 → `ChunkLoadError`.
 *
 * Next.js's `reset()` cannot fix this: it re-renders the same route segment and
 * re-requests the very same missing chunk. The only reliable recovery is a hard
 * navigation that re-fetches fresh HTML pointing at the current chunk names.
 */

const RELOAD_TIMESTAMP_KEY = 'bossaz:chunk-reload-at';

// If a reload was attempted within this window and the chunk error recurs, the
// chunk is genuinely unreachable (offline, stuck Service Worker, expired skew
// window). Stop reloading and let the caller render its normal fallback so we
// never get stuck in a reload loop.
const RELOAD_GUARD_WINDOW_MS = 10_000;

export function isChunkLoadError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;

  const { name, message } = error as { name?: unknown; message?: unknown };

  if (name === 'ChunkLoadError') return true;

  return (
    typeof message === 'string' && /Loading chunk .+ failed/i.test(message)
  );
}

const readLastReloadAt = (): number => {
  try {
    const raw = window.sessionStorage.getItem(RELOAD_TIMESTAMP_KEY);

    return raw ? Number(raw) || 0 : 0;
  } catch {
    return 0;
  }
};

// Returns whether the guard was persisted. A persisted guard is what makes the
// reload one-shot, so the caller must not reload when this returns false.
const writeLastReloadAt = (value: number): boolean => {
  try {
    window.sessionStorage.setItem(RELOAD_TIMESTAMP_KEY, String(value));

    return true;
  } catch {
    // sessionStorage unavailable (private mode / disabled / quota).
    return false;
  }
};

/**
 * If `error` is a `ChunkLoadError`, trigger a one-time hard reload and return
 * `true`. Returns `false` for any other error, when a reload was already
 * attempted within the guard window, or when the guard cannot be persisted
 * (so the caller renders its fallback).
 *
 * `now` is injectable to keep the time-based guard deterministic in tests.
 */
export function recoverFromChunkLoadError(
  error: unknown,
  now: number = Date.now()
): boolean {
  if (!isChunkLoadError(error)) return false;

  const lastReloadAt = readLastReloadAt();

  if (lastReloadAt && now - lastReloadAt < RELOAD_GUARD_WINDOW_MS) {
    return false;
  }

  // If the guard cannot be persisted, a storage-less client would reload on
  // every fresh load → infinite loop. Skip the reload and let the fallback show.
  if (!writeLastReloadAt(now)) {
    return false;
  }

  window.location.reload();

  return true;
}
